import { applyKeyframe } from "../../core/keyframeHandler";
import { getSceneState } from "../../stores/sceneStore";
import { getBodyTree } from "../../stores/sceneActions/bodyUtils";
import type { AllActions } from "../../stores/sceneActions/actionTypes";
import type { BodyUUID, Joint, Quaternion, Vector3 } from "../../types/scene";
import { findBodyForJointUuid } from "../bodyHierarchy";

function qposWidthForJoint(joint: Joint): number {
  if (joint.kind === "free") return 7;
  if (joint.kind === "ball") return 4;
  if (joint.kind === "hinge" || joint.kind === "slide") return 1;
  return 0;
}

export function applyFreeJointRootPosesFromQpos(
  qpos: number[],
  joints: Joint[],
  sceneActions: AllActions
): void {
  if (!Array.isArray(qpos) || qpos.length === 0) return;
  const expectedQposWidth = joints.reduce((sum, joint) => sum + qposWidthForJoint(joint), 0);
  if (expectedQposWidth <= 0 || qpos.length < expectedQposWidth) return;

  const sceneState = getSceneState();
  let cursor = 0;
  joints.forEach((joint) => {
    if (!joint) return;
    if (joint.kind === "free") {
      if (cursor + 7 <= qpos.length) {
        const bodyUuid = findBodyForJointUuid(sceneState.bodies || {}, joint.uuid);
        const position = qpos.slice(cursor, cursor + 3).map(Number) as Vector3;
        const orientation = qpos.slice(cursor + 3, cursor + 7).map(Number) as Quaternion;
        if (
          bodyUuid &&
          position.length === 3 &&
          orientation.length === 4 &&
          position.every(Number.isFinite) &&
          orientation.every(Number.isFinite)
        ) {
          sceneActions.setBodyTransform(bodyUuid as BodyUUID, position, orientation);
        }
      }
      cursor += 7;
      return;
    }
    cursor += qposWidthForJoint(joint);
  });
}

export function orderImportedJointsForQpos(
  importedJoints: Joint[],
  sceneState: ReturnType<typeof getSceneState> = getSceneState()
): Joint[] {
  const importedByUuid = new Map<string, Joint>();
  importedJoints.forEach((joint) => {
    if (joint?.uuid) importedByUuid.set(String(joint.uuid), joint);
  });
  if (importedByUuid.size === 0) return [];

  const ordered: Joint[] = [];
  const seen = new Set<string>();
  const visitBody = (body: { joints?: unknown[]; children?: unknown[] } | null | undefined) => {
    if (!body) return;
    (body.joints || []).forEach((jointRef) => {
      const jointUuid = typeof jointRef === "string" ? jointRef : "";
      const joint = importedByUuid.get(jointUuid);
      if (!joint || seen.has(jointUuid)) return;
      ordered.push(joint);
      seen.add(jointUuid);
    });
    (body.children || []).forEach((child) =>
      visitBody(child as { joints?: unknown[]; children?: unknown[] })
    );
  };

  getBodyTree(sceneState).forEach((root) =>
    visitBody(root as { joints?: unknown[]; children?: unknown[] })
  );

  importedJoints.forEach((joint) => {
    const jointUuid = joint?.uuid ? String(joint.uuid) : "";
    if (!jointUuid || seen.has(jointUuid)) return;
    ordered.push(joint);
    seen.add(jointUuid);
  });

  return ordered;
}

export function applyHomeKeyframeToImportedJoints(
  homeQpos: number[] | undefined,
  importedJoints: Joint[],
  sceneActions: AllActions
): void {
  if (!Array.isArray(homeQpos) || homeQpos.length === 0) return;
  try {
    const sceneState = getSceneState();
    const jointsRecord = (sceneState.joints || {}) as Record<string, Joint | undefined>;
    const jointsForHome = importedJoints
      .map((j) => (j?.uuid ? jointsRecord[String(j.uuid)] : undefined))
      .filter((j: Joint | undefined): j is Joint => Boolean(j));
    if (jointsForHome.length === 0) return;
    const orderedJoints = orderImportedJointsForQpos(jointsForHome, sceneState);
    applyFreeJointRootPosesFromQpos(homeQpos.map(Number), orderedJoints, sceneActions);
    applyKeyframe(
      homeQpos,
      orderedJoints,
      [],
      sceneActions.updateJointValue,
      sceneActions.updateBallJointQuat ?? null
    );
  } catch {
    // Not critical - if this fails, joints stay at their default ref values.
  }
}
